import React, { useEffect, useState } from 'react';
import { colors } from 'app/shared/theme/colors';
import { useTranslation } from 'app/shared/i18n';
import { useAppSelector } from 'app/config/store';
import axios from 'axios';

/** Check if a line contains emoji — highlight these lines in PROMOTION */
const lineHasEmoji = (line: string): boolean => {
  // Check each codepoint for emoji ranges (avoids eslint no-misleading-character-class)
  for (const ch of line) {
    const cp = ch.codePointAt(0) || 0;
    if (
      (cp >= 0x1f300 && cp <= 0x1faf8) || // emoticons, symbols, flags
      (cp >= 0x2600 && cp <= 0x27bf) || // misc symbols, dingbats
      (cp >= 0x2702 && cp <= 0x27b0) || // dingbats
      cp === 0x2764 ||
      cp === 0x2665 || // hearts
      cp === 0x2b50 || // star
      (cp >= 0x23f0 && cp <= 0x23fa)
    ) {
      // clock symbols
      return true;
    }
  }
  return false;
};

/** Render promotion content with highlighted emoji lines */
const PromoContent = ({ text, linkColor }: { text: string; linkColor: string }) => {
  const lines = text.split('\n');
  return (
    <>
      {lines.map((line, i) => {
        const hasEmoji = lineHasEmoji(line);
        return (
          <div
            key={i}
            style={{
              padding: hasEmoji ? '8px 14px' : '3px 0',
              marginBottom: hasEmoji ? 6 : 2,
              borderRadius: hasEmoji ? 10 : 0,
              background: hasEmoji ? 'rgba(34,197,94,0.06)' : 'transparent',
              borderLeft: hasEmoji ? '2px solid rgba(34,197,94,0.4)' : 'none',
              textAlign: 'left',
              fontSize: 14,
              fontWeight: 400,
              color: hasEmoji ? colors.text : colors.muted,
            }}
          >
            <Linkify text={line} linkColor={linkColor} />
          </div>
        );
      })}
    </>
  );
};

/** Parse URLs in text and render as clickable links */
const Linkify = ({ text, linkColor }: { text: string; linkColor: string }) => {
  const urlPattern = /(https?:\/\/[^\s<>"']+)/g;
  const parts = text.split(urlPattern);
  const isUrl = (s: string) => /^https?:\/\//.test(s);
  return (
    <>
      {parts.map((part, i) =>
        isUrl(part) ? (
          <a
            key={i}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: linkColor,
              textDecoration: 'underline',
              textUnderlineOffset: 3,
              fontWeight: 600,
              wordBreak: 'break-all',
              transition: 'opacity .15s',
            }}
            onMouseEnter={e => (e.currentTarget.style.opacity = '0.8')}
            onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
            onClick={e => e.stopPropagation()}
          >
            {part}
          </a>
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        ),
      )}
    </>
  );
};

interface Announcement {
  id: number;
  title: string;
  content: string;
  type: string;
  showOnce: boolean;
  allowDismiss: boolean;
  priority: number;
  repeatIntervalHours: number;
  audience: string; // ALL, GUESTS_ONLY, USERS_ONLY, TARGETED
}

const PERM_PREFIX = 'ann-perm-'; // permanent dismiss per user (checkbox ticked)
const TIMED_PREFIX = 'ann-timed-'; // timed dismiss with timestamp per user
const SESSION_PREFIX = 'ann-session-'; // session dismiss per user
const SHARED_PREFIX = 'ann-shared-'; // shared dismiss for ALL audience (survives guest→login transition)
const MAX_ENTRIES = 200;

const getUserKey = () => {
  try {
    return sessionStorage.getItem('ann-current-user') || 'guest';
  } catch {
    return 'guest';
  }
};
const setUserKey = (login: string) => {
  try {
    sessionStorage.setItem('ann-current-user', login);
  } catch {
    /* ignore */
  }
};

/** Permanently dismissed (checkbox ticked) — never shows again */
const getPermDismissed = (): number[] => {
  try {
    return JSON.parse(localStorage.getItem(PERM_PREFIX + getUserKey()) || '[]');
  } catch {
    return [];
  }
};
const addPermDismissed = (id: number) => {
  let list = getPermDismissed();
  if (!list.includes(id)) {
    list.push(id);
    if (list.length > MAX_ENTRIES) list = list.slice(-MAX_ENTRIES);
    localStorage.setItem(PERM_PREFIX + getUserKey(), JSON.stringify(list));
  }
};

/** Timed dismiss — stores timestamp, respects repeatIntervalHours */
const getTimedDismissed = (): Record<string, number> => {
  try {
    return JSON.parse(localStorage.getItem(TIMED_PREFIX + getUserKey()) || '{}');
  } catch {
    return {};
  }
};
const addTimedDismissed = (id: number) => {
  const map = getTimedDismissed();
  map[String(id)] = Date.now();
  // Cleanup old entries
  const keys = Object.keys(map);
  if (keys.length > MAX_ENTRIES) {
    const sorted = keys.sort((a, b) => map[a] - map[b]);
    sorted.slice(0, keys.length - MAX_ENTRIES).forEach(k => delete map[k]);
  }
  localStorage.setItem(TIMED_PREFIX + getUserKey(), JSON.stringify(map));
};
const isTimedDismissExpired = (id: number, repeatHours: number): boolean => {
  if (repeatHours <= 0) return false; // no repeat = use session dismiss
  const map = getTimedDismissed();
  const ts = map[String(id)];
  if (!ts) return true; // never dismissed
  return Date.now() - ts > repeatHours * 3600000;
};

/** Session dismiss — per-user, cleared on logout, used for repeatInterval=0 non-ALL */
const getSessionDismissed = (): number[] => {
  try {
    return JSON.parse(sessionStorage.getItem(SESSION_PREFIX + getUserKey()) || '[]');
  } catch {
    return [];
  }
};
const addSessionDismissed = (id: number) => {
  const list = getSessionDismissed();
  if (!list.includes(id)) {
    list.push(id);
    sessionStorage.setItem(SESSION_PREFIX + getUserKey(), JSON.stringify(list));
  }
};

/** Shared session dismiss — device-level (survives guest↔login within same tab session) */
const SHARED_SESSION_KEY = 'ann-shared-session';
const getSharedSessionDismissed = (): number[] => {
  try {
    return JSON.parse(sessionStorage.getItem(SHARED_SESSION_KEY) || '[]');
  } catch {
    return [];
  }
};
const addSharedSessionDismissed = (id: number) => {
  const list = getSharedSessionDismissed();
  if (!list.includes(id)) {
    list.push(id);
    sessionStorage.setItem(SHARED_SESSION_KEY, JSON.stringify(list));
  }
};

/** Shared dismiss for ALL audience — uses device-level key (not per-user)
 *  so guest dismiss persists when same person logs in */
const getSharedDismissed = (): number[] => {
  try {
    return JSON.parse(localStorage.getItem(SHARED_PREFIX + 'device') || '[]');
  } catch {
    return [];
  }
};
const addSharedDismissed = (id: number) => {
  let list = getSharedDismissed();
  if (!list.includes(id)) {
    list.push(id);
    if (list.length > MAX_ENTRIES) list = list.slice(-MAX_ENTRIES);
    localStorage.setItem(SHARED_PREFIX + 'device', JSON.stringify(list));
  }
};
const getSharedTimedDismissed = (): Record<string, number> => {
  try {
    return JSON.parse(localStorage.getItem(SHARED_PREFIX + 'timed') || '{}');
  } catch {
    return {};
  }
};
const addSharedTimedDismissed = (id: number) => {
  const map = getSharedTimedDismissed();
  map[String(id)] = Date.now();
  const keys = Object.keys(map);
  if (keys.length > MAX_ENTRIES) {
    const sorted = keys.sort((a, b) => map[a] - map[b]);
    sorted.slice(0, keys.length - MAX_ENTRIES).forEach(k => delete map[k]);
  }
  localStorage.setItem(SHARED_PREFIX + 'timed', JSON.stringify(map));
};
const isSharedTimedExpired = (id: number, repeatHours: number): boolean => {
  if (repeatHours <= 0) return false;
  const map = getSharedTimedDismissed();
  const ts = map[String(id)];
  if (!ts) return true;
  return Date.now() - ts > repeatHours * 3600000;
};

/* ═══ CSS Animations (injected once) ═══ */
const ANIMATIONS = `
@keyframes annFadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
@keyframes annOverlay { from { opacity: 0; } to { opacity: 1; } }
@keyframes annPulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.12); }
}
@keyframes annFloat {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
}
@keyframes annShimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}
@keyframes annGlow {
  0%, 100% { box-shadow: 0 0 20px rgba(34,197,94,0.15); }
  50% { box-shadow: 0 0 40px rgba(34,197,94,0.35), 0 0 60px rgba(22,163,74,0.15); }
}
@keyframes annRainbow {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}
@keyframes annSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
@keyframes annBorderGlow {
  0%, 100% { border-color: rgba(59,130,246,0.2); }
  50% { border-color: rgba(59,130,246,0.5); }
}
@keyframes annStarTwinkle {
  0%, 100% { opacity: 0.2; transform: scale(0.8); }
  50% { opacity: 0.6; transform: scale(1.2); }
}
@keyframes annSlideUp {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes annConfetti1 { 0% { transform: translate(0,0) rotate(0); opacity:1; } 100% { transform: translate(-30px,-60px) rotate(180deg); opacity:0; } }
@keyframes annConfetti2 { 0% { transform: translate(0,0) rotate(0); opacity:1; } 100% { transform: translate(20px,-50px) rotate(-120deg); opacity:0; } }
@keyframes annConfetti3 { 0% { transform: translate(0,0) rotate(0); opacity:1; } 100% { transform: translate(35px,-40px) rotate(200deg); opacity:0; } }
`;

/* ═══════════════════════════════════════════
   PROMOTION — festive gold with confetti particles + glow
   ═══════════════════════════════════════════ */
const PromotionTemplate = ({ a, footer }: { a: Announcement; onClose: () => void; footer: React.ReactNode }) => (
  <div
    style={{
      background: colors.bgCard,
      borderRadius: 20,
      overflow: 'hidden',
      border: `1px solid ${colors.border}`,
      boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
    }}
  >
    {/* Thin accent line */}
    <div
      style={{
        height: 3,
        background: 'linear-gradient(90deg, #22c55e, #16a34a, #22c55e)',
      }}
    />
    <div style={{ padding: '36px 36px 24px', textAlign: 'center' }}>
      <div style={{ fontSize: 40, marginBottom: 14 }}>🎁</div>
      <div
        style={{
          display: 'inline-block',
          padding: '4px 16px',
          borderRadius: 999,
          marginBottom: 20,
          background: `rgba(34,197,94,0.1)`,
          border: '1px solid rgba(34,197,94,0.2)',
          fontSize: 11,
          fontWeight: 600,
          color: '#22c55e',
          textTransform: 'uppercase',
          letterSpacing: '.06em',
        }}
      >
        KHUYẾN MÃI
      </div>
      <h3
        style={{
          fontSize: 19,
          fontWeight: 600,
          marginBottom: 20,
          color: colors.text,
          lineHeight: 1.4,
          fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
        }}
      >
        {a.title}
      </h3>
      <div style={{ lineHeight: 1.8, padding: '0 8px', textAlign: 'left' }}>
        <PromoContent text={a.content} linkColor="#22c55e" />
      </div>
    </div>
    {footer}
  </div>
);

/* ═══════════════════════════════════════════
   UPDATE — technical blue with spinning gear + pulsing border
   ═══════════════════════════════════════════ */
const UpdateTemplate = ({ a, footer }: { a: Announcement; onClose: () => void; footer: React.ReactNode }) => (
  <div
    style={{
      background: colors.bgCard,
      borderRadius: 16,
      overflow: 'hidden',
      border: '1px solid rgba(59,130,246,0.25)',
      animation: 'annBorderGlow 3s ease-in-out infinite',
      boxShadow: '0 20px 60px rgba(0,0,0,0.5), 0 0 30px rgba(59,130,246,0.06)',
    }}
  >
    <div
      style={{
        padding: '24px 28px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 16,
        background: 'linear-gradient(180deg, rgba(59,130,246,0.1), transparent)',
        borderBottom: '1px solid rgba(59,130,246,0.15)',
      }}
    >
      <div
        style={{
          width: 48,
          height: 48,
          borderRadius: 14,
          flexShrink: 0,
          background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 24,
          boxShadow: '0 4px 16px rgba(59,130,246,0.35)',
        }}
      >
        <span style={{ animation: 'annSpin 4s linear infinite' }}>⚙️</span>
      </div>
      <div>
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: '#3b82f6',
            textTransform: 'uppercase',
            letterSpacing: '.06em',
            marginBottom: 4,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: '#3b82f6',
              animation: 'annPulse 1.5s ease-in-out infinite',
              display: 'inline-block',
            }}
          />
          CẬP NHẬT HỆ THỐNG
        </div>
        <h3
          style={{
            fontSize: 17,
            fontWeight: 500,
            color: colors.text,
            margin: 0,
            lineHeight: 1.4,
            fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
          }}
        >
          {a.title}
        </h3>
      </div>
    </div>
    <div style={{ padding: '22px 28px' }}>
      <div
        style={{
          fontSize: 14,
          color: colors.muted,
          lineHeight: 1.8,
          whiteSpace: 'pre-wrap',
          padding: '16px 20px',
          borderRadius: 10,
          background: colors.surface,
          border: `1px solid ${colors.border}`,
          borderLeft: '3px solid #3b82f6',
          animation: 'annSlideUp 0.5s ease 0.3s both',
        }}
      >
        <Linkify text={a.content} linkColor="#60a5fa" />
      </div>
    </div>
    {footer}
  </div>
);

/* ═══════════════════════════════════════════
   ADVERTISEMENT — neon purple with twinkling stars + gradient text
   ═══════════════════════════════════════════ */
const AdvertisementTemplate = ({ a, footer }: { a: Announcement; onClose: () => void; footer: React.ReactNode }) => (
  <div
    style={{
      background: '#0f0a1a',
      borderRadius: 20,
      overflow: 'hidden',
      border: '1px solid rgba(168,85,247,0.3)',
      boxShadow: '0 20px 60px rgba(0,0,0,0.6), 0 0 50px rgba(168,85,247,0.1)',
    }}
  >
    <div
      style={{
        padding: '40px 36px 28px',
        textAlign: 'center',
        background: 'linear-gradient(135deg, rgba(139,92,246,0.2), rgba(236,72,153,0.12), rgba(168,85,247,0.08))',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Subtle twinkling stars */}
      {['✦', '✧', '✦'].map((star, i) => (
        <span
          key={i}
          style={{
            position: 'absolute',
            color: '#a855f7',
            opacity: 0.3,
            fontSize: [12, 10, 14][i],
            top: [15, 50, 75][i] + '%',
            left: [8, 90, 15][i] + '%',
            animation: `annStarTwinkle ${2 + i * 0.5}s ease-in-out infinite`,
            animationDelay: `${i * 0.3}s`,
            pointerEvents: 'none',
          }}
        >
          {star}
        </span>
      ))}

      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '7px 22px',
          borderRadius: 999,
          marginBottom: 16,
          background: 'linear-gradient(135deg, #8b5cf6, #ec4899)',
          boxShadow: '0 4px 20px rgba(139,92,246,0.5)',
          animation: 'annFloat 3s ease-in-out infinite',
        }}
      >
        <span style={{ fontSize: 16, animation: 'annPulse 2s ease-in-out infinite' }}>📢</span>
        <span style={{ fontSize: 11, fontWeight: 600, color: '#fff', letterSpacing: '.06em' }}>QUẢNG CÁO</span>
      </div>
      <h3
        style={{
          fontSize: 18,
          fontWeight: 500,
          margin: '0 0 4px',
          lineHeight: 1.3,
          color: '#e2d4f0',
          fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
        }}
      >
        {a.title}
      </h3>
    </div>
    <div style={{ padding: '22px 36px 24px' }}>
      <div
        style={{
          fontSize: 14,
          color: '#b8a5d0',
          lineHeight: 1.8,
          whiteSpace: 'pre-wrap',
          animation: 'annSlideUp 0.5s ease 0.2s both',
        }}
      >
        <Linkify text={a.content} linkColor="#c084fc" />
      </div>
    </div>
    {footer}
  </div>
);

/* ═══════════════════════════════════════════
   INFO — clean green with slide-in content + subtle check animation
   ═══════════════════════════════════════════ */
const InfoTemplate = ({ a, footer }: { a: Announcement; onClose: () => void; footer: React.ReactNode }) => (
  <div
    style={{
      background: colors.bgCard,
      borderRadius: 16,
      overflow: 'hidden',
      border: `1px solid ${colors.success}25`,
      boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
    }}
  >
    <div style={{ padding: '28px 28px 22px', display: 'flex', gap: 16 }}>
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: '50%',
          flexShrink: 0,
          background: `${colors.success}15`,
          border: `2px solid ${colors.success}33`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 22,
          animation: 'annFloat 4s ease-in-out infinite',
        }}
      >
        ℹ️
      </div>
      <div style={{ flex: 1 }}>
        <div
          style={{
            fontSize: 10,
            fontWeight: 600,
            color: colors.success,
            textTransform: 'uppercase',
            letterSpacing: '.08em',
            marginBottom: 6,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: colors.success,
              animation: 'annPulse 2s ease-in-out infinite',
              display: 'inline-block',
            }}
          />
          THÔNG BÁO
        </div>
        <h3
          style={{
            fontSize: 16,
            fontWeight: 500,
            color: colors.text,
            margin: '0 0 10px',
            lineHeight: 1.4,
            fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
          }}
        >
          {a.title}
        </h3>
        <div
          style={{
            fontSize: 14,
            color: colors.muted,
            lineHeight: 1.7,
            whiteSpace: 'pre-wrap',
            animation: 'annSlideUp 0.4s ease 0.2s both',
          }}
        >
          <Linkify text={a.content} linkColor={colors.success} />
        </div>
      </div>
    </div>
    {footer}
  </div>
);

/* ═══════════════════════════════════════════
   Main Popup Component
   ═══════════════════════════════════════════ */
export const AnnouncementPopup = () => {
  const { t } = useTranslation();
  const account = useAppSelector(state => state.authentication.account);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Set user key: logged-in user or 'guest'
    const login = account?.login || 'guest';
    setUserKey(login);

    // Clear session dismiss on fresh login (not page refresh)
    const loginFlagKey = `ann-login-flag-${login}`;
    if (!sessionStorage.getItem(loginFlagKey)) {
      try {
        sessionStorage.removeItem(SESSION_PREFIX + login);
      } catch {
        /* ignore */
      }
      sessionStorage.setItem(loginFlagKey, '1');
    }

    axios
      .get('/api/announcements')
      .then(res => {
        const permDismissed = getPermDismissed();
        const sessionDismissedIds = getSessionDismissed();
        const sharedDismissed = getSharedDismissed();
        const sharedSessionIds = getSharedSessionDismissed();
        const filtered = (res.data || []).filter((a: Announcement) => {
          const isAll = !a.audience || a.audience === 'ALL';

          // For ALL audience: use SHARED dismiss (device-level, survives guest↔login)
          if (isAll) {
            if (sharedDismissed.includes(a.id)) return false; // permanently dismissed (checkbox ticked)
            const repeatHours = a.repeatIntervalHours || 0;
            if (repeatHours > 0) {
              return isSharedTimedExpired(a.id, repeatHours);
            }
            // No repeat + ALL: shared session dismiss (same key for guest AND user)
            if (sharedSessionIds.includes(a.id)) return false;
            return true;
          }

          // For non-ALL audience: use per-user dismiss
          if (permDismissed.includes(a.id)) return false;

          const repeatHours = a.repeatIntervalHours || 0;
          if (repeatHours > 0) {
            return isTimedDismissExpired(a.id, repeatHours);
          } else {
            if (sessionDismissedIds.includes(a.id)) return false;
          }
          return true;
        });
        if (filtered.length > 0) {
          setAnnouncements(filtered);
          setCurrentIdx(0);
          setVisible(true);
        }
      })
      .catch(() => {});
  }, [account?.login]);

  const current = announcements[currentIdx];
  if (!visible || !current) return null;

  const handleClose = () => {
    const isAll = !current.audience || current.audience === 'ALL';
    const repeatHours = current.repeatIntervalHours || 0;

    if (current.showOnce || (dontShowAgain && current.allowDismiss)) {
      // Permanent dismiss
      if (isAll) {
        addSharedDismissed(current.id); // device-level: survives guest→login
      } else {
        addPermDismissed(current.id); // per-user
      }
    } else if (repeatHours > 0) {
      // Timed dismiss: reappear after N hours
      if (isAll) {
        addSharedTimedDismissed(current.id);
      } else {
        addTimedDismissed(current.id);
      }
    } else {
      // Session dismiss only
      if (isAll) {
        addSharedSessionDismissed(current.id); // shared: survives guest↔login in same tab
      } else {
        addSessionDismissed(current.id); // per-user
      }
    }
    if (currentIdx < announcements.length - 1) {
      setCurrentIdx(currentIdx + 1);
      setDontShowAgain(false);
    } else {
      setVisible(false);
    }
  };

  const typeColors: Record<string, string> = {
    PROMOTION: '#22c55e',
    UPDATE: '#3b82f6',
    ADVERTISEMENT: '#a855f7',
    INFO: colors.success,
  };
  const accent = typeColors[current.type] || colors.success;
  const isLast = currentIdx >= announcements.length - 1;

  const footer = (
    <div
      style={{
        padding: '16px 28px',
        borderTop: `1px solid ${accent}15`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: 'rgba(0,0,0,0.1)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {current.allowDismiss && !current.showOnce ? (
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12, color: colors.dim }}>
            <input
              type="checkbox"
              checked={dontShowAgain}
              onChange={e => setDontShowAgain(e.target.checked)}
              style={{ accentColor: accent, cursor: 'pointer' }}
            />
            {t('announcement.dontShowAgain')}
          </label>
        ) : (
          <div />
        )}
        {announcements.length > 1 && (
          <span style={{ fontSize: 11, color: colors.dim, fontFamily: 'system-ui, -apple-system, sans-serif' }}>
            {currentIdx + 1}/{announcements.length}
          </span>
        )}
      </div>
      <button
        onClick={handleClose}
        style={{
          padding: '8px 24px',
          borderRadius: 10,
          fontSize: 13,
          fontWeight: 500,
          background: accent,
          color: '#fff',
          border: 'none',
          cursor: 'pointer',
          transition: 'all .2s',
          boxShadow: `0 2px 8px ${accent}33`,
        }}
        onMouseEnter={e => {
          e.currentTarget.style.transform = 'translateY(-1px)';
          e.currentTarget.style.boxShadow = `0 5px 20px ${accent}55`;
        }}
        onMouseLeave={e => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = `0 3px 12px ${accent}44`;
        }}
      >
        {isLast ? t('announcement.understood') : `${t('announcement.next')} →`}
      </button>
    </div>
  );

  const TemplateComponent =
    {
      PROMOTION: PromotionTemplate,
      UPDATE: UpdateTemplate,
      ADVERTISEMENT: AdvertisementTemplate,
      INFO: InfoTemplate,
    }[current.type] || InfoTemplate;

  return (
    <>
      <style>{ANIMATIONS}</style>

      <div
        onClick={current.allowDismiss ? handleClose : undefined}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.65)',
          backdropFilter: 'blur(6px)',
          zIndex: 9998,
          animation: 'annOverlay .25s ease',
          cursor: current.allowDismiss ? 'pointer' : 'default',
        }}
      />

      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          pointerEvents: 'none',
          animation: 'annFadeIn .2s ease-out',
        }}
      >
        <div
          onClick={e => e.stopPropagation()}
          style={{
            width: '92%',
            maxWidth: 520,
            pointerEvents: 'auto',
          }}
        >
          <TemplateComponent a={current} onClose={handleClose} footer={footer} />
        </div>
      </div>
    </>
  );
};
